/**
 * CompanyDashboardInitiativeTable
 *
 * Displays initiatives in a rich table with chat popovers, action menus (edit/delete),
 * status badges, and tooltip handling for delinked rows. Integrates permissions to
 * conditionally expose actions. Emits row clicks for parent-controlled drawers.
 */
import React, { useState } from "react";
import Status from "../../../request-dashboard/request/status/status";
import { MUITable } from "../../../../common/components";
import "./company-dashboard-initiative-table.scss";
import { TableHeader } from "../../../../common/components/mui-table/mui-table";
import editIcons from "../../../../assets/icons/editIcons.svg";
import deleteRedIcons from "../../../../assets/icons/delete-red.svg";
import { usePermissions } from "../../../../common/hooks/usePermissions";
import MODULES from "../../../../constants/modules";
import {
  addComment,
  deleteInitiativeById,
  getCommentsById,
} from "../../../../api/initiative";
import dayjs from "dayjs";
import DeleteConfirmationPopup from "../../../../common/components/delete-confirmation-popup/delete-confirmation-popup";
import { CommentType } from "../../../../common/components/chat-popover/comment";
import Tooltip from "@mui/material/Tooltip";
import reviewIcon from "../../../../assets/icons/under_review_icon.svg";

const headers: TableHeader[] = [
  { key: "initiativeTitle", label: "Title" },
  { key: "objectives", label: "Objectives" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "progress", label: "Progress (%)" },
  { key: "status", label: "Status" },
];

const transformCommentData = (apiComment: any): CommentType => {
  let html = apiComment.content;
  if (apiComment.filePathJson) {
    try {
      const jsonString =
        typeof apiComment.filePathJson === "string"
          ? apiComment.filePathJson
          : JSON.stringify(apiComment.filePathJson);

      const filePaths = JSON.parse(jsonString); // validate
      let fileHtml = "";
      if (Array.isArray(filePaths)) {
        filePaths.forEach((filePath) => {
          const isImage = ["jpg", "jpeg", "png", "gif"].some((ext) =>
            filePath.toLowerCase().endsWith(ext),
          );
          if (isImage) {
            fileHtml += `<br><img src="${filePath}" alt="attachment" style="max-width: 200px; max-height: 200px;"/>`;
          } else {
            fileHtml += `<br><a href="${filePath}" target="_blank" rel="noopener noreferrer">${filePath}</a>`;
          }
        });
      }
      html += fileHtml;
    } catch (e) {
      console.error(e);
    }
  }

  return {
    id: apiComment.commentId.toString(),
    author: {
      id: apiComment.authorName, // No author ID from API, using name
      name: apiComment.authorName,
    },
    html: html,
    timestamp: apiComment.createdAt,
    replies: apiComment.replies
      ? apiComment.replies.map(transformCommentData)
      : [],
  };
};

const CompanyDashboardInitiativeTable: React.FC<any> = (props) => {
  const {
    onRowClick,
    initiativeData,
    setIsShowAddInitiativeTableDrawer,
    setInitiativeId,
    setObjectiveId,
    setAlertMessage,
    fetchInitiatives,
    setAlertType,
    setAlertOpen,
    setLoading,
  } = props;
  const [selectedMenuOption, setSelectedMenuOption] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deletePopupId, setDeletePopupId] = useState<any>(null);
  const [chatComments, setChatComments] = useState<Record<number, any[]>>({});
  const [activeChatRow, setActiveChatRow] = useState<number | null>(null);

  const {
    canWrite: canWriteInitiatives,
    canEdit: canEditInitiatives,
    canDelete: canDeleteInitiatives,
  } = usePermissions(MODULES.INITIATIVES);

  const handleOpenEditPopup = (data: any) => {
    setInitiativeId(data?.initiativeId);
    setObjectiveId(data?.objectiveId);
    setIsShowAddInitiativeTableDrawer(true);
    setSelectedMenuOption(null);
  };

  const handleDelete = async () => {
    if (!deletePopupId) return;

    try {
      await deleteInitiativeById(deletePopupId);
      fetchInitiatives();
      setAlertMessage("Initiative deleted successfully");
      setAlertType("success");
      setAlertOpen(true);
      setIsDeletePopupOpen(false);
      setDeletePopupId(null);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to delete initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setIsDeletePopupOpen(false);
      setDeletePopupId(null);
    }
  };

  const handleOpenChat = async (rowId: number | null) => {
    setActiveChatRow(rowId);
    if (!rowId) return;
    if (!chatComments[rowId]) {
      try {
        const res = await getCommentsById(rowId);
        const transformedComments = (res.data.data.comments || []).map(
          transformCommentData,
        );
        setChatComments((prev) => ({ ...prev, [rowId]: transformedComments }));
      } catch {
        setChatComments((prev) => ({ ...prev, [rowId]: [] }));
      }
    }
  };

  const handleCommentSubmit = async (
    rowId: number,
    html: string,
    parentId: string | null,
    attachments: string[],
  ) => {
    try {
      const data = {
        initiativeId: rowId,
        notes: html,
        parentComment: parentId,
        tagJson: {},
        filePathJson: JSON.stringify(attachments),
        createdBy: null,
      };
      await addComment(data);
      const res = await getCommentsById(rowId);
      const transformedComments = (res.data.data.comments || []).map(
        transformCommentData,
      );
      setChatComments((prev) => ({ ...prev, [rowId]: transformedComments }));
    } catch {
      setAlertMessage("Failed to add comment");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const deriveSortableValue = (value: any): string | number | undefined => {// NOSONAR
    if (value == null) {
      return undefined;
    }

    if (typeof value === "string" || typeof value === "number") {
      return value;
    }

    if (React.isValidElement(value)) {
      const props = (value.props || {}) as Record<string, unknown>;
      const candidateKeys = [
        "data-sort-value",
        "dataSortValue",
        "dataValue",
        "status",
        "title",
        "label",
        "name",
        "value",
        "children",
      ];

      for (const key of candidateKeys) {
        if (!Object.hasOwn(props, key)) continue;
        const candidate = props[key];
        if (candidate == null || candidate === "") {
          continue;
        }
        if (typeof candidate === "string" || typeof candidate === "number") {
          return candidate;
        }
        if (Array.isArray(candidate)) {
          const extracted = candidate
            .map((child) => deriveSortableValue(child))
            .filter(
              (val): val is string | number =>
                typeof val === "string" || typeof val === "number",
            );
          if (extracted.length > 0) {
            return typeof extracted[0] === "number"
              ? extracted[0]
              : extracted.join(" ");
          }
        }
        if (React.isValidElement(candidate)) {
          const extracted = deriveSortableValue(candidate);
          if (extracted !== undefined) {
            return extracted;
          }
        }
      }

      const children = props.children;
      if (typeof children === "string" || typeof children === "number") {
        return children;
      }
      if (Array.isArray(children)) {
        const extracted = children
          .map((child) => deriveSortableValue(child))
          .filter(
            (val): val is string | number =>
              typeof val === "string" || typeof val === "number",
          );
        if (extracted.length > 0) {
          return typeof extracted[0] === "number"
            ? extracted[0]
            : extracted.join(" ");
        }
      }
    }

    if (typeof value === "object") {
      const objectValue = (value as Record<string, unknown>)?.text;
      if (typeof objectValue === "string" || typeof objectValue === "number") {
        return objectValue;
      }
    }

    return undefined;
  };

  return (
    <>
      <MUITable
        columnNames={headers}
        rowsData={
          initiativeData?.map((item: any) => {
            const hasObjectives = item.objectives?.length > 0;
            const isApprovalPending = item.isApproved === false;
            const isFaded = !hasObjectives || isApprovalPending;
            const objectivesLabel = hasObjectives
              ? item.objectives.map((obj: any) => obj.objectiveName).join(", ")
              : "-";

            const sortValues: Record<string, string | number> = {
              initiativeTitle: item.initiativeTitle ?? "",
              objectives: objectivesLabel,
              progress: Number.isFinite(item.progress) ? item.progress : 0,
              status: item.status ?? "",
            };
            const row = {
              ...item,
              owners: item?.owners,
              isInitiativePage: true,
              progress: `${Math.round(item.progress || 0)}%`,
              status: <Status status={item.status} />,
              startDate: item?.initiativeStartDate  ? dayjs(item?.initiativeStartDate).format("MM-DD-YYYY") : "-",
              endDate: item?.initiativeEndDate ? dayjs(item?.initiativeEndDate).format("MM-DD-YYYY") : "-",
              objectives: hasObjectives ? (
                <div
                  title={item.objectives
                    .map((obj: any) => obj.objectiveName)
                    .join(", ")}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                  }}
                >
                  {objectivesLabel}
                </div>
              ) : (
                <div style={{ color: "#999" }}>-</div>
              ),
              isFaded,
              isApprovalPending,
              __sortValues: sortValues,
            };
            if (isFaded) {
              // Wrap all cell values in a Tooltip for faded rows
              const tooltipColumns = new Set(
                headers.map((header) => header.key),
              );

              tooltipColumns.forEach((key) => {
                if (!(key in row)) return;

                const cell = row[key];
                const sortableValue = deriveSortableValue(cell);
                const spanProps =
                  sortableValue !== undefined && sortableValue !== null
                    ? {
                        "data-sort-value": String(sortableValue),
                        dataSortValue: String(sortableValue),
                      }
                    : ({} as Record<string, unknown>);
                const dateCell= (cell ? dayjs(cell).format("MM-DD-YYYY") : "-");
                row[key] = (key === "startDate" || key === "endDate") ? 
                 dateCell : (
                  <Tooltip
                    title="This initiative is delinked and cannot be modified."
                    placement="top"
                  >
                    <button
                      style={{ display: "inline-block", width: "100%", border: "none", background: "none", padding: "0px 16px", margin: 0, textAlign: "left", cursor: "not-allowed" }}
                      {...spanProps}
                    >
                      {cell}
                    </button>
                  </Tooltip>
                );
              });
            }
            return row;
          }) || []
        }
        showChatColumn={true}
        onRowClick={onRowClick}
        showActionsColumnWithOptions={
          canEditInitiatives && canDeleteInitiatives
        }
        menuOptions={[
          ...(canEditInitiatives
            ? [
                {
                  key: "edit",
                  label: "Edit",
                  onClick: (close: any, data: any) => {
                    close();
                    handleOpenEditPopup(data);
                  },
                  icon: editIcons,
                },
              ]
            : []),
          ...(canDeleteInitiatives
            ? [
                {
                  key: "delete",
                  label: <div style={{ marginLeft: "5px" }}>Delete</div>,
                  onClick: (close: any, data: any) => {
                    close();
                    setIsDeletePopupOpen(true);
                    setDeletePopupId(data?.initiativeId);
                  },
                  icon: (
                    <img
                      src={deleteRedIcons}
                      alt="Delete"
                      style={{ width: 15, height: 15 }}
                    />
                  ),
                },
              ]
            : []),
        ]}
        selectedMenuOption={selectedMenuOption}
        setSelectedMenuOption={setSelectedMenuOption}
        onOpenChat={handleOpenChat}
        commentsByRow={chatComments}
        activeChatRow={activeChatRow}
        onCommentSubmit={handleCommentSubmit}
        canWrite={canWriteInitiatives}
        fromInitiativeTable={true}
        noRecordsTitle="No Initiatives available to display."
        noRecordsMessage="Please reload the page, or add a new Initiative."
        renderExpandedRow={(row: any) => {
          if (row.isApprovalPending) {
            return (
              <div
                style={{
                  marginTop: "5px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <img alt="Under review icon" src={reviewIcon} />
                {" "}
                Under review
              </div>
            );
          }
          return null;
        }}
      />
      {isDeletePopupOpen && (
        <DeleteConfirmationPopup
          isOpen={true}
          onClose={() => {
            setIsDeletePopupOpen(false);
            setDeletePopupId(null);
          }}
          handleDelete={handleDelete}
          deleteId={deletePopupId}
        />
      )}
    </>
  );
};

export default CompanyDashboardInitiativeTable;
